import React from 'react';
import {TooltipProps} from 'recharts';
import {LineChartData} from './LineChart';
import {BarChartData} from './BarChart';
import {KpiDataPackage, KpiPackage, TooltipComparisonMode, VisibleAttribute} from '../../types/Chart';
import {px} from 'csx';
import {formatValue, numberFormat, percentFormat} from '../../utility/FormatValueUtils';
import {DateRange} from '../../stores/TimeIntervalStore';
import moment, {Moment} from 'moment';
import {colorToString} from '../../utility/ColorUtils';
import {kpiPackagesMatch} from '../../stores/GeneralLineDiagramStore';
import {BlackColorFaded05, LightGreyColor, WhiteColorFaded80} from '../../styles/colors';

export enum ChartType {
    LINE_CHART = 'lineChart',
    BAR_CHART = 'barChart',
}

type CustomTooltipPayload<T extends TooltipProps<any, any>['payload']> = T extends Array<unknown>
    ? T[0] & {
          payload?: BarChartData | LineChartData;
      }
    : never;

interface TooltipContentProps extends TooltipProps<any, any> {
    kpiPackageToString?: (kpiPackage: KpiPackage, payload: BarChartData | LineChartData) => string | React.ReactNode;
    visibleAttribute: VisibleAttribute[];
    payload?: Array<CustomTooltipPayload<any>>;
    chartType: ChartType;
    lineChartData?: LineChartData[];
    tooltipComparisonModeIfOneElementIsSelected?: TooltipComparisonMode;
    mainDateRange?: DateRange;
    compareDateRange?: DateRange;
    currencyFormatting: (value: number) => string;
}

/**
 * Checks, if the compare mode is active and percent values are compared to numerical values. If so, the percent
 * conversion should be prohibited and the decimal value of the percent value should be used for comparison
 * @param {boolean} selectionCompareActive
 * @param {VisibleAttribute[]} visibleAttributes
 * @returns {boolean}
 */
const calcUseRateFormatting = (selectionCompareActive: boolean, visibleAttributes: VisibleAttribute[]) => {
    if (selectionCompareActive) {
        const rateNumCount = visibleAttributes.reduce(
            (rateNumAgg, visibleAttribute) => {
                if (visibleAttribute.kpiRequestInfo.isRate) {
                    rateNumAgg.rate++;
                } else {
                    rateNumAgg.num++;
                }
                return rateNumAgg;
            },
            {rate: 0, num: 0}
        );
        if (rateNumCount.rate > 0 && rateNumCount.num > 0) {
            return false;
        }
    }

    return true;
};

export default class TooltipContent extends React.Component<TooltipContentProps, {}> {
    private getNameForAttributeIfPossible(kpiPackage: KpiPackage, payload: BarChartData | LineChartData): string | React.ReactNode {
        if (this.props.kpiPackageToString !== undefined) {
            return this.props.kpiPackageToString(kpiPackage, payload);
        }
        return kpiPackage.channel.name + kpiPackage.searchtermOrProductId + kpiPackage.kpi;
    }

    private getValue(
        payload: BarChartData | LineChartData,
        kpiPackage: KpiPackage,
        useAbsoluteValue = true,
        selectionCompareActive: boolean,
        useRateFormatting: boolean,
        moneyFormatting: boolean
    ): number | string {
        const matchingData = payload.data.find((data) => kpiPackagesMatch(data, kpiPackage));
        if (matchingData !== undefined) {
            const valueToUse = useAbsoluteValue ? matchingData.value : matchingData.relValue;
            const showValueAsPercent = matchingData.isRate || !useAbsoluteValue;
            if (showValueAsPercent && useRateFormatting) {
                if (this.props.tooltipComparisonModeIfOneElementIsSelected === TooltipComparisonMode.Percent) {
                    return valueToUse < 0
                        ? `-${formatValue(Math.abs(valueToUse), percentFormat(0, 3))}`
                        : `+${formatValue(Math.abs(valueToUse), percentFormat(0, 3))}`;
                }
                return formatValue(valueToUse, percentFormat(0, 3));
            }
            return selectionCompareActive && valueToUse > 0
                ? `+${formatValue(valueToUse, this.chooseValueFormatting(moneyFormatting))}`
                : formatValue(valueToUse, this.chooseValueFormatting(moneyFormatting));
        }
        return 0;
    }

    private chooseValueFormatting(useMoneyFormatting: boolean) {
        if (useMoneyFormatting) {
            return this.props.currencyFormatting;
        }
        return numberFormat(0, 3);
    }

    private getComparePayloadDate(payload: LineChartData): {comparePayloadDate: Moment | null; fullPayload: LineChartData} {
        const hasCompareData = this.props.visibleAttribute.some((visibleAttribute) => visibleAttribute.kpiRequestInfo.isCompareDiagram);
        const {mainDateRange, compareDateRange, lineChartData} = this.props;
        if (hasCompareData && mainDateRange && compareDateRange && lineChartData) {
            const diffFromStartDate = moment(payload.date).diff(mainDateRange.startDate, 'seconds');
            const comparePayloadDate = moment(compareDateRange.startDate).add(diffFromStartDate, 'seconds');
            const copyPayload = {...payload};
            const comparePayloadEntry = lineChartData.find((chartData) => chartData.date.isSame(comparePayloadDate));
            if (comparePayloadEntry) {
                copyPayload.data = [
                    ...copyPayload.data.filter((compareLessPayload) => !compareLessPayload.isCompareDiagram),
                    ...comparePayloadEntry.data.filter((comparePayload) => comparePayload.isCompareDiagram),
                ];
            }
            return {comparePayloadDate, fullPayload: copyPayload};
        }

        return {comparePayloadDate: null, fullPayload: payload};
    }

    private getChartTooltipContent(payload: LineChartData): React.ReactNode {
        const {comparePayloadDate, fullPayload} = this.getComparePayloadDate(payload);

        return (
            <div>
                <table>
                    <tbody>{this.renderVisibleAttributes(fullPayload)}</tbody>
                </table>
                <div style={{textAlign: 'right', fontSize: px(12), fontWeight: 'bold'}}>
                    {fullPayload.date.format('L')} &nbsp;
                    {fullPayload.date.format('LTS')}
                </div>
                {comparePayloadDate !== null ? (
                    <div style={{textAlign: 'right', fontSize: px(12), fontWeight: 'bold', color: BlackColorFaded05}}>
                        {comparePayloadDate.format('L')} &nbsp;
                        {comparePayloadDate.format('LTS')}
                    </div>
                ) : null}
            </div>
        );
    }

    private getBarChartTooltipContent(payload: BarChartData): React.ReactNode {
        return (
            <table>
                <tbody>{this.renderVisibleAttributes(payload)}</tbody>
            </table>
        );
    }

    private getSingleSelectedItem(): VisibleAttribute | undefined {
        const matches = this.props.visibleAttribute.filter((visibleAttribute: VisibleAttribute) => visibleAttribute.opacity === 1);
        if (matches.length === 1 && this.props.visibleAttribute.length > 1) {
            return matches[0];
        } else {
            return undefined;
        }
    }

    /**
     * If one bar/line gets selected, the tooltip will change. The selected entry will be set to the top,
     * the remaining entries will be shown as absolute difference (+-) to the selected entry
     */
    private calculateRelativePayloadToSelectedItem(
        selectedItem: VisibleAttribute,
        payload: BarChartData | LineChartData
    ): BarChartData | LineChartData {
        const newPayloadData: KpiDataPackage[] = [];
        const selectedPayloadData = payload.data.find((data) => kpiPackagesMatch(data, selectedItem.kpiRequestInfo));
        if (selectedPayloadData) {
            newPayloadData.push(selectedPayloadData);
            payload.data
                .filter((data) => !kpiPackagesMatch(data, selectedItem.kpiRequestInfo))
                .forEach((data) => {
                    const dataClone = {...data};
                    switch (this.props.tooltipComparisonModeIfOneElementIsSelected) {
                        case TooltipComparisonMode.Percent:
                            dataClone.value = dataClone.value = (dataClone.value - selectedPayloadData.value) / selectedPayloadData.value;
                            dataClone.isRate = true;
                            break;
                        case TooltipComparisonMode.Absolute:
                            dataClone.value = dataClone.value - selectedPayloadData.value;
                            break;
                        default:
                            throw Error('Invalid tooltip comparison mode encountered ' + this.props.tooltipComparisonModeIfOneElementIsSelected);
                    }
                    newPayloadData.push(dataClone);
                });

            return {
                ...payload,
                data: newPayloadData,
            };
        }
        return payload;
    }

    private sortSelectedItemToTheTopOfVisibleAttributes(singleSelectedItem: VisibleAttribute) {
        const unselectedItems = this.props.visibleAttribute.filter(
            (data) => !kpiPackagesMatch(data.kpiRequestInfo, singleSelectedItem.kpiRequestInfo)
        );
        return [singleSelectedItem, ...unselectedItems];
    }

    private updatePayloadAndVisibleAttributesIfCompareToSingleSelectedItemIsNecessary(
        payload: BarChartData | LineChartData
    ): {payload: BarChartData | LineChartData; visibleAttribute: VisibleAttribute[]; selectionCompareActive: boolean} {
        const singleSelectedItem = this.getSingleSelectedItem();
        const comparisonMode = this.props.tooltipComparisonModeIfOneElementIsSelected;
        if (comparisonMode && comparisonMode !== TooltipComparisonMode.Disabled && singleSelectedItem) {
            return {
                payload: this.calculateRelativePayloadToSelectedItem(singleSelectedItem, payload),
                visibleAttribute: this.sortSelectedItemToTheTopOfVisibleAttributes(singleSelectedItem),
                selectionCompareActive: true,
            };
        }
        return {payload, visibleAttribute: this.props.visibleAttribute, selectionCompareActive: false};
    }

    private renderVisibleAttributes(payload: BarChartData | LineChartData): React.ReactNode {
        const {
            payload: payloadCopy,
            visibleAttribute: visibleAttributes,
            selectionCompareActive,
        } = this.updatePayloadAndVisibleAttributesIfCompareToSingleSelectedItemIsNecessary(payload);

        const useRateFormatting = calcUseRateFormatting(selectionCompareActive, visibleAttributes);

        return visibleAttributes.map((visibleAttribute, index) => {
            const {kpiRequestInfo, color, useAbsValue, moneyFormatting} = visibleAttribute;
            const {kpi, searchtermOrProductId, channel, isCompareDiagram, variantId, testId} = kpiRequestInfo;
            return (
                <tr
                    className="label"
                    style={{color: colorToString(color)}}
                    key={kpi + searchtermOrProductId + channel.name + isCompareDiagram + variantId + testId}>
                    <td style={{textAlign: 'right', paddingRight: 5}}>{`${this.getValue(
                        payloadCopy,
                        kpiRequestInfo,
                        useAbsValue,
                        selectionCompareActive && index !== 0,
                        useRateFormatting,
                        moneyFormatting || false
                    )}`}</td>
                    <td>{this.getNameForAttributeIfPossible(kpiRequestInfo, payloadCopy)}</td>
                </tr>
            );
        });
    }

    private getContentBody(payload: LineChartData | BarChartData): React.ReactNode {
        switch (this.props.chartType) {
            case ChartType.LINE_CHART:
                return this.getChartTooltipContent(payload as LineChartData);
            case ChartType.BAR_CHART:
                return this.getBarChartTooltipContent(payload as BarChartData);
            default:
                throw Error('Unknown chart type used ' + this.props.chartType);
        }
    }

    public render(): React.ReactNode {
        const {active, payload: arrPayload} = this.props;
        if (active && arrPayload && arrPayload.length > 0) {
            const {payload} = arrPayload[0];
            return (
                <div
                    style={{
                        background: WhiteColorFaded80,
                        borderRadius: 1,
                        textAlign: 'left',
                        padding: 5,
                        border: '1px solid ' + LightGreyColor,
                        minHeight: 50,
                        minWidth: 150,
                    }}>
                    {this.getContentBody(payload)}
                </div>
            );
        }
        return null;
    }
}



// WEBPACK FOOTER //
// ./src/views/charts/TooltipContent.tsx